import { ReactElement, useMemo } from 'react';

import CircularLoader from '@/components/CircularLoader.tsx';
import CollapsableItem from '@/components/CollapsableItem.tsx';
import CollapsableSection from '@/components/CollapsableSection.tsx';
import { GroupedAppointments } from '@/components/PatientModal/grouped-appointments.tsx';
import Paragraph from '@/components/text/Paragraph.tsx';

import usePrismic from '@/hooks/usePrismic.tsx';

import { GenericPatient } from '@/models/GenericPatient.ts';
import { PrismicDataType } from '@/models/PrismicHookTypes.ts';
import { Appointment, CollectionActionScope, DailyAppointment } from '@/models/appointment/Appointment.ts';
import { CancelAppointmentOptions } from '@/models/appointment/CancelAppointmentOptions.ts';
import { EnrichedPractitionerData } from '@/models/appointment/EnrichedPractitionerData.ts';

import {
    getAppointmentsExcludingSeriesUpdateCancelled,
    groupAppointmentsBySeries,
    isCanceledAppointment,
    isFutureAppointment,
    isSeriesInactiveDueToCancellation,
} from '@/utils/appointmentUtils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';
import { openAppointmentScheduler } from '@/utils/modal/appointmentSchedulerUtils.tsx';
import { openCollectionEditScopeModal } from '@/utils/modalUtils.tsx';

interface Props {
    patientAppointments?: {
        future: Appointment[];
        past: Appointment[];
    };
    mutatePatientAppointments: () => void;
    isLoading: boolean;
    patient?: GenericPatient; // Patient object
    enrichedPractitionerData?: EnrichedPractitionerData; // Enriched practitioner data
    onDelete: (
        appointment: Appointment,
        scope: CollectionActionScope | undefined,
        cancelOption: CancelAppointmentOptions
    ) => void;
    onAppointmentClick?: (appointment: DailyAppointment) => void;
}

export const PatientModalAppointments = ({
    patientAppointments,
    mutatePatientAppointments,
    isLoading,
    patient,
    enrichedPractitionerData,
    onDelete,
    onAppointmentClick,
}: Props) => {
    const { data: professionalProfiles } = usePrismic(PrismicDataType.PROFESSIONAL_PROFILE);

    const handleEdit = (apt: Appointment): void => {
        const openScheduler = (isSingleCollectionEdit?: boolean, editingCollectionId?: string): void => {
            openAppointmentScheduler({
                mode: 'edit',
                existing_appointment: apt,
                patient: patient,
                enriched_practitioner_data: enrichedPractitionerData,
                isSingleCollectionEdit: isSingleCollectionEdit,
                editingCollectionId: editingCollectionId,
                onClose: mutatePatientAppointments,
            });
        };

        if (apt.collection_id) {
            openCollectionEditScopeModal(
                (scope) => {
                    if (scope === CollectionActionScope.SINGLE) {
                        openScheduler(true);
                    } else {
                        openScheduler(undefined, apt.collection_id);
                    }
                },
                apt,
                patient?.instance_id
            );
            return;
        }

        openScheduler();
    };

    // Combine appointments, excluding ones cancelled via series update (replaced by new appointments)
    const groupedBySeries = useMemo(() => {
        const combinedAppointments = [...(patientAppointments?.future ?? []), ...(patientAppointments?.past ?? [])];

        const allAppointments = getAppointmentsExcludingSeriesUpdateCancelled(combinedAppointments);

        return groupAppointmentsBySeries(allAppointments);
    }, [patientAppointments?.future, patientAppointments?.past]);

    // Split series into future/past
    const now = dayjs();
    const futureSeries: Record<string, Appointment[]> = {};
    const pastSeries: Record<string, Appointment[]> = {};

    for (const [seriesId, appointments] of Object.entries(groupedBySeries)) {
        const sorted = [...appointments].sort((a, b) => dayjs(a.start).valueOf() - dayjs(b.start).valueOf());

        const hasFuture = sorted.some((apt) => isFutureAppointment(now, apt));
        const isInactiveDueToCancellation = isSeriesInactiveDueToCancellation(appointments, now);
        if (hasFuture && !isInactiveDueToCancellation) futureSeries[seriesId] = sorted;
        else pastSeries[seriesId] = sorted;
    }

    const futureGroups = Object.values(futureSeries);
    const pastGroups = Object.values(pastSeries);

    const futureCount = futureGroups.reduce(
        (sum, group) =>
            sum + group.filter((apt) => isFutureAppointment(now, apt) && !isCanceledAppointment(apt)).length,
        0
    );
    const pastCount = pastGroups.reduce((total, group) => total + group.length, 0);

    const renderGroupedAppointments = (
        series: Record<string, Appointment[]>,
        groups: Appointment[][],
        futureAppointments: boolean
    ): ReactElement => {
        if (isLoading)
            return (
                <div className="flex justify-center">
                    <CircularLoader />
                </div>
            );
        if (groups.length === 0)
            return (
                <Paragraph>{futureAppointments ? 'Keine anstehenden Termine' : 'Keine vergangenen Termine'}</Paragraph>
            );

        return (
            <GroupedAppointments
                isSeriesUnfinished={futureAppointments}
                groupedAppointments={series}
                professionalProfiles={professionalProfiles}
                onEdit={handleEdit}
                onDelete={onDelete}
                onAppointmentClick={onAppointmentClick}
                futureAppointments={patientAppointments?.future}
            />
        );
    };

    return (
        <CollapsableSection>
            <CollapsableItem title={`Anstehende Termine (${futureCount})`} isLoading={isLoading}>
                {renderGroupedAppointments(futureSeries, futureGroups, true)}
            </CollapsableItem>

            <CollapsableItem title={`Vergangene Termine (${pastCount})`} isLoading={isLoading}>
                {renderGroupedAppointments(pastSeries, pastGroups, false)}
            </CollapsableItem>
        </CollapsableSection>
    );
};
